export const offlineKey = (ownerId: string, path: string) => `offline:${ownerId}:${path}`;
// Downloaded copies are separate from unsent work: listing the send queue must
// not load every project photograph into memory.
async function access<T>(mode: IDBTransactionMode, action: (store: IDBObjectStore) => IDBRequest<T>): Promise<T | null> {
  const db = await new Promise<IDBDatabase>((resolve, reject) => {
    let expired = false;
    const timer = setTimeout(() => { expired = true; reject(new Error('Offline storage timed out')); }, 3000);
    try {
      const request = indexedDB.open('revival-site-offline-cache', 1);
      request.onupgradeneeded = () => { request.result.createObjectStore('copies'); };
      request.onsuccess = () => { clearTimeout(timer); if (expired) request.result.close(); else resolve(request.result); };
      request.onerror = request.onblocked = () => { clearTimeout(timer); expired = true; reject(request.error); };
    } catch (reason) { clearTimeout(timer); reject(reason); }
  }).catch(() => null);
  if (!db) return null;
  try { return await new Promise<T | null>((resolve) => {
    const tx = db.transaction('copies', mode);
    const timer = setTimeout(() => { try { tx.abort(); } catch {} resolve(null); }, 3000);
    let result: T | null = null;
    tx.oncomplete = () => { clearTimeout(timer); resolve(result); };
    tx.onerror = tx.onabort = () => { clearTimeout(timer); resolve(null); };
    try { const request = action(tx.objectStore('copies')); request.onsuccess = () => { result = request.result ?? null; }; }
    catch { clearTimeout(timer); try { tx.abort(); } catch {} resolve(null); }
  }); } finally { db.close(); }
}
export async function cachedValue<T>(ownerId: string, path: string): Promise<T | null> {
  if (!ownerId) return null;
  return access<T>('readonly', store => store.get(offlineKey(ownerId, path)));
}
export async function cacheValue<T>(ownerId: string, path: string, value: T) {
  return ownerId ? await access('readwrite', store => store.put(value, offlineKey(ownerId, path))) !== null : false;
}
export async function hasCached(ownerId: string, path: string) {
  return Boolean(ownerId && await access('readonly', store => store.getKey(offlineKey(ownerId, path))));
}
export async function cachedPaths(ownerId: string, prefix: string): Promise<string[]> {
  if (!ownerId) return [];
  const start = offlineKey(ownerId, prefix);
  const keys = await access('readonly', store => store.getAllKeys(IDBKeyRange.bound(start, `${start}\uffff`)));
  return (keys ?? []).map(key => String(key).slice(offlineKey(ownerId, '').length));
}
export async function forgetCached(ownerId: string, path: string) {
  if (ownerId) await access('readwrite', store => store.delete(offlineKey(ownerId, path)));
}
export const disconnected = () => typeof navigator !== 'undefined' && navigator.onLine === false;

// Only transport failures may use an offline snapshot. A server denial or a
// programming error must remain visible and must never grant cached access.
export class NetworkError extends Error {}
export function isNetworkFailure(reason: unknown) {
  return reason instanceof NetworkError || reason instanceof TypeError && /fetch|network|load failed|lost.*response/i.test(reason.message)
    || reason instanceof DOMException && ['AbortError', 'TimeoutError'].includes(reason.name);
}
